<template>
  <v-container class="blog-post-container">
    <v-btn variant="text" class="mb-4" @click="$router.push({ name: 'blog' })">
      <v-icon start>mdi-arrow-left</v-icon>
      {{ $t('blog.backToBlog') }}
    </v-btn>

    <!-- Loading -->
    <v-progress-circular
      v-if="loadingPost"
      indeterminate
      color="primary"
      class="d-block mx-auto my-12"
    />

    <template v-else-if="post">
      <!-- Header Image -->
      <v-img
        v-if="post.headerImage"
        :src="post.headerImage"
        :alt="post.title"
        height="360"
        cover
        class="rounded-lg mb-6"
        gradient="to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.4)"
      />

      <!-- Post Header -->
      <div class="mb-6">
        <v-chip
          :color="post.categoryColor"
          variant="flat"
          size="small"
          class="mb-3"
          @click="$router.push({ name: 'blog', query: { category: post.categorySlug } })"
        >
          <v-icon start size="14">{{ post.categoryIcon }}</v-icon>
          {{ $t(`blog.categories.${post.categorySlug}`) }}
        </v-chip>

        <h1 class="text-h3 font-weight-bold mb-3">{{ post.title }}</h1>

        <!-- Author + Meta -->
        <div class="d-flex align-center flex-wrap ga-4 text-grey">
          <div class="d-flex align-center ga-2">
            <v-avatar size="36">
              <v-img v-if="post.avatarUrl" :src="post.avatarUrl" />
              <v-icon v-else>mdi-account-circle</v-icon>
            </v-avatar>
            <div>
              <router-link
                :to="{ name: 'profile', params: { username: post.username } }"
                class="text-body-1 font-weight-medium text-decoration-none"
              >
                {{ post.displayName || post.username }}
              </router-link>
              <div class="text-caption">
                {{ formatDate(post.published_at) }}
              </div>
            </div>
          </div>
          <blog-share-button :slug="post.slug" :title="post.title" class="mt-4" />
          <v-spacer />

          <!-- Stats -->
          <div class="d-flex align-center ga-4 text-caption">
            <span><v-icon size="14">mdi-eye</v-icon> {{ post.viewCount }}</span>
            <span><v-icon size="14">mdi-comment</v-icon> {{ post.commentCount }}</span>
            <span><v-icon size="14">mdi-clock-outline</v-icon> {{ post.readTime }} min</span>
          </div>
        </div>
      </div>

      <v-divider class="mb-6" />

      <!-- Post Body (rich text from editor) -->
      <div class="blog-body text-body-1 mb-8">
        <VuetifyViewer :value="post.body" :extensions="viewerExtensions" />
      </div>

      <!-- Reactions Bar -->
      <blog-reactions
        :post-id="post.postId"
        :reactions="post.reactions"
        :user-reaction="post.userReaction"
        :total="post.reactionCount"
        @react="handlePostReaction"
      />

      <v-divider class="my-6" />

      <!-- Edit/Delete if own post -->
      <div v-if="isOwnPost" class="d-flex ga-2 mb-6">
        <v-btn variant="outlined" size="small" @click="showEditDialog = true">
          <v-icon start size="16">mdi-pencil</v-icon>
          {{ $t('blog.edit') }}
        </v-btn>
        <v-btn variant="outlined" size="small" color="error" @click="confirmDelete">
          <v-icon start size="16">mdi-delete</v-icon>
          {{ $t('blog.delete') }}
        </v-btn>
      </div>

      <!-- Comments Section -->
      <h2 class="text-h5 mb-4">
        <v-icon start>mdi-comment-multiple</v-icon>
        {{ $t('blog.comments') }} ({{ post.commentCount }})
      </h2>

      <!-- New comment form -->
      <blog-comment-form
        v-if="currentUserId"
        :post-id="post.postId"
        class="mb-6"
        @submitted="onCommentAdded"
      />
      <v-alert v-else type="info" variant="tonal" class="mb-6">
        <router-link to="/login">{{ $t('blog.loginToComment') }}</router-link>
      </v-alert>

      <!-- Threaded comments -->
      <blog-comment
        v-for="comment in post.comments"
        :key="comment.commentId"
        :comment="comment"
        :post-id="post.postId"
        @reply="onCommentAdded"
        @deleted="onCommentDeleted"
      />

      <v-alert v-if="post.comments.length === 0" type="info" variant="tonal" class="mt-2">
        {{ $t('blog.noComments') }}
      </v-alert>
    </template>

    <!-- Not found -->
    <v-alert v-else type="warning" class="mt-8">
      {{ $t('blog.postNotFound') }}
    </v-alert>

    <!-- Edit dialog -->
    <blog-post-editor
      v-if="post"
      v-model="showEditDialog"
      :categories="categories"
      :editing="post"
      @saved="onPostEdited"
    />
  </v-container>
</template>

<script setup>
/* eslint-disable */
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import {
  BaseKit, Bold, Italic, Underline, Strike,
  Color, Highlight, Heading, Link, Image,
  Video, Table, History,
  VuetifyViewer
} from 'vuetify-pro-tiptap'
import 'vuetify-pro-tiptap/style.css'
import BlogReactions from '../components/blog/BlogReactions.vue'
import BlogComment from '../components/blog/BlogComment.vue'
import BlogCommentForm from '../components/blog/BlogCommentForm.vue'
import BlogPostEditor from '../components/blog/BlogPostEditor.vue'
import BlogShareButton from '../components/blog/BlogShareButton.vue'

const viewerExtensions = [
  BaseKit, Bold, Italic, Underline, Strike,
  Color, Highlight, Heading, Link, Image,
  Video, Table, History
]

const store = useStore()
const route = useRoute()
const router = useRouter()

const post = computed(() => store.getters['blog/currentPost'])
const loadingPost = computed(() => store.getters['blog/loadingPost'])
const categories = computed(() => store.getters['blog/categories'])
const isOwnPost = computed(() => {
  if (!post.value || !currentUserId.value) return false
  return post.value.userId === parseInt(currentUserId.value)
})

const currentUserId = computed(() => {
  const storeId = store.getters['auth/userId']
  if (storeId) return storeId
  try {
    const user = JSON.parse(localStorage.getItem('user'))
    return user?.userId || null
  } catch {
    return null
  }
})

const showEditDialog = ref(false)

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('da-DK', {
    year: 'numeric', month: 'long', day: 'numeric'
  })
}

const handlePostReaction = async (reactionType) => {
  await store.dispatch('blog/toggleReaction', { postId: post.value.postId, reactionType })
  // Refresh to get updated reaction counts
  store.dispatch('blog/fetchPost', route.params.slug)
}

const onCommentAdded = () => {
  // Refresh post to get updated comments
  store.dispatch('blog/fetchPost', route.params.slug)
}

const onCommentDeleted = () => {
  store.dispatch('blog/fetchPost', route.params.slug)
}

const onPostEdited = () => {
  showEditDialog.value = false
  store.dispatch('blog/fetchPost', route.params.slug)
}

const confirmDelete = async () => {
  if (confirm('Er du sikker på at du vil slette dette indlæg?')) {
    await store.dispatch('blog/deletePost', post.value.postId)
    router.push({ name: 'blog' })
  }
}

onMounted(() => {
  store.dispatch('blog/fetchCategories')
  store.dispatch('blog/fetchPost', route.params.slug)
})
</script>

<style scoped>
.blog-post-container {
  max-width: 860px;
  margin: 0 auto;
  padding-top: 1.5rem;
}
.blog-body {
  line-height: 1.8;
}
.blog-body :deep(p) {
  margin-bottom: 1rem;
}
.blog-body :deep(.mention) {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;
}
</style>